import { useEffect, useRef, useState } from 'react';

/**
 * 03 · Watch it build — a scripted, video-style replay of an RCRT Studio
 * session, rendered as a faithful replica of the real Studio UI
 * (rcrt-studio: top bar with workspace/project selectors, Build/Backend/
 * Code/Event Trace white-pill tabs, Build-plan phase rail, preview header
 * with the Live badge, and the Backend inspector "your app, as breadcrumbs"
 * with counted tabs). Studio's own dark-navy palette is used inside the
 * player; the marketing phosphor stays on the player chrome.
 *
 * Honesty: scripted animation of the real build loop, labelled as such.
 */

type BackendKind = 'agent' | 'tool' | 'service' | 'knowledge';

interface BackendItem {
  kind: BackendKind;
  name: string;
  slug: string;
  tags: string[];
}

const PHASES = ['requirements', 'architecture', 'frontend', 'review', 'build verify', 'done'];

interface Step {
  d: number;
  chat?: { role: 'user' | 'agent' | 'sys'; text: string };
  backend?: BackendItem;
  tab?: 'Build' | 'Backend';
  phase?: number; // index into PHASES
  preview?: number; // 1..3
  live?: boolean;
}

const SCRIPT: Step[] = [
  { d: 2600, phase: 0, tab: 'Build', chat: { role: 'user', text: 'Build me a CRM + project hub for our agency. Contacts from Google Workspace, a deals pipeline, project kanban, and a Friday digest to Slack.' } },
  { d: 2200, phase: 1, chat: { role: 'agent', text: 'On it. Planning the build: requirements → architecture → frontend → review → build verify. Connecting your services first.' }, tab: 'Backend' },
  { d: 1600, chat: { role: 'sys', text: 'service.connect google-workspace ✓ oauth granted' }, backend: { kind: 'service', name: 'Google Workspace', slug: 'service-google-workspace', tags: ['interpret:service-grant'] } },
  { d: 1600, chat: { role: 'sys', text: 'service.connect slack ✓ bot token vaulted' }, backend: { kind: 'service', name: 'Slack', slug: 'service-slack', tags: ['interpret:service-grant'] } },
  { d: 2000, chat: { role: 'agent', text: 'Backend next — the data model is just breadcrumbs: contact, deal, project, task. The tag is the table.' } },
  { d: 1500, chat: { role: 'sys', text: 'tool.create lead-capture ✓ deno sandbox' }, backend: { kind: 'tool', name: 'Lead Capture', slug: 'tool-lead-capture', tags: ['interpret:executable'] } },
  { d: 1500, chat: { role: 'sys', text: 'tool.create pipeline-rollup ✓' }, backend: { kind: 'tool', name: 'Pipeline Rollup', slug: 'tool-pipeline-rollup', tags: ['interpret:executable'] } },
  { d: 1500, chat: { role: 'sys', text: 'tool.create digest-send ✓ schedule: fri 16:00' }, backend: { kind: 'tool', name: 'Friday Digest', slug: 'tool-digest-send', tags: ['interpret:executable', 'interpret:schedule'] } },
  { d: 1600, chat: { role: 'sys', text: 'agent.create crm-advisor ✓ memory: LOD' }, backend: { kind: 'agent', name: 'CRM Advisor', slug: 'crm-advisor', tags: ['interpret:promptable'] } },
  { d: 1700, chat: { role: 'sys', text: 'knowledge.ingest sales-playbook.md ✓ 42 chunks' }, backend: { kind: 'knowledge', name: 'Sales Playbook', slug: 'sales-playbook', tags: ['interpret:knowledge'] } },
  { d: 2100, phase: 2, chat: { role: 'agent', text: 'Frontend scaffolded — wiring the pipeline board to live breadcrumbs.' }, tab: 'Build', preview: 1 },
  { d: 1900, preview: 2, chat: { role: 'sys', text: 'ui.compose pipeline-board ✓ contacts-table ✓' } },
  { d: 1900, phase: 3, preview: 3, chat: { role: 'sys', text: 'review ✓ RCRT-compliance critique passed' } },
  { d: 1700, phase: 4, chat: { role: 'sys', text: 'ci.verify build ✓ tests ✓ deploy → cloud run' } },
  { d: 3200, phase: 5, live: true, chat: { role: 'agent', text: 'Deployed. Your CRM is live — invite the team, or ask me to add invoicing next.' } },
];

const TOTAL = SCRIPT.reduce((s, x) => s + x.d, 0);

/** The CRM being "built" in the preview — plausible agency deals, no real customer data. */
const PIPELINE = [
  {
    name: 'New',
    total: '$64k',
    deals: [
      { co: 'Nimbus Health', what: 'Website rebuild', val: '$18k', ini: 'NH', av: 1 },
      { co: 'Kōwhai Legal', what: 'CRM setup', val: '$9.5k', ini: 'KL', av: 2 },
      { co: 'Harbour Freight', what: 'Brand + site', val: '$36.5k', ini: 'HF', av: 3 },
    ],
  },
  {
    name: 'Qualified',
    total: '$118k',
    deals: [
      { co: 'Aurora Retail', what: 'Ecommerce build', val: '$54k', ini: 'AR', av: 4 },
      { co: 'Southern Dairy Co', what: 'Supplier portal', val: '$64k', ini: 'SD', av: 1 },
    ],
  },
  {
    name: 'Won',
    total: '$86k',
    deals: [
      { co: 'Beacon Insurance', what: 'Retainer', val: '$6.5k/mo', ini: 'BI', av: 2 },
      { co: 'Fern & Field', what: 'Campaign site', val: '$22k', ini: 'FF', av: 4 },
    ],
  },
];

function fmt(ms: number) {
  const s = Math.floor(ms / 1000);
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

export default function StudioDemo() {
  const [elapsed, setElapsed] = useState(0);
  const [playing, setPlaying] = useState(true);
  const [visible, setVisible] = useState(false);
  const chatRef = useRef<HTMLDivElement>(null);
  const sectionRef = useRef<HTMLElement>(null);

  // Pause the interval when the section is off-screen so the periodic
  // re-renders and eventual reset don't shift page height while the user
  // is reading a different section.
  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      (entries) => {
        const isVisible = entries[0].isIntersecting;
        setVisible(isVisible);
        if (isVisible) setElapsed(0);
      },
      { threshold: 0.1 },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  useEffect(() => {
    if (!playing || !visible) return;
    const iv = setInterval(() => {
      setElapsed((e) => (e + 100 >= TOTAL ? 0 : e + 100));
    }, 100);
    return () => clearInterval(iv);
  }, [playing, visible]);

  let acc = 0;
  let stepIdx = -1;
  for (let i = 0; i < SCRIPT.length; i++) {
    if (elapsed >= acc) stepIdx = i;
    acc += SCRIPT[i].d;
  }
  const active = SCRIPT.slice(0, stepIdx + 1);
  const chat = active.filter((s) => s.chat).map((s) => s.chat!);
  const backend = active.filter((s) => s.backend).map((s) => s.backend!);
  const tab = [...active].reverse().find((s) => s.tab)?.tab ?? 'Build';
  const phase = Math.max(0, ...active.map((s) => s.phase ?? 0));
  const preview = Math.max(0, ...active.map((s) => s.preview ?? 0));
  const live = active.some((s) => s.live);

  const counts: Record<BackendKind, number> = { agent: 0, tool: 0, service: 0, knowledge: 0 };
  backend.forEach((b) => (counts[b.kind] += 1));
  const dataCount = backend.length * 4;

  useEffect(() => {
    if (chatRef.current) chatRef.current.scrollTop = chatRef.current.scrollHeight;
  }, [chat.length]);

  return (
    <section id="demo" className="tint" ref={sectionRef}>
      <div className="wrap">
        <div className="sec-head">
          <div className="sec-num">03 · Watch it build</div>
          <div>
            <h2>
              A CRM in <span className="ph">one session</span>, not one quarter.
            </h2>
            <p className="lede">
              This is RCRT Studio — the real app, replayed: describe the product, watch the
              builder agents connect services, compose tools, agents and knowledge as
              breadcrumbs, wire the frontend, and deploy. Scripted animation of the real
              build loop — not a mock-up of features we don't have.
            </p>
          </div>
        </div>

        <div className="demo-player">
          <div className="demo-topbar">
            <span className="demo-dots">
              <i /><i /><i />
            </span>
            <span className="demo-title">rcrt-studio…run.app/project</span>
            <span className="demo-rec">
              <i className="rec-dot" /> replay
            </span>
          </div>

          {/* ── Studio replica ── */}
          <div className="st-app">
            <div className="st-top">
              <span className="st-logo">✦</span>
              <span className="st-brand">RCRT Studio</span>
              <span className="st-sel">▢ Development Workspace ▾</span>
              <span className="st-sel st-proj">agency-hub ▾</span>
              <div className="st-tabs">
                {(['Build', 'Backend', 'Code', 'Event Trace'] as const).map((t) => (
                  <span key={t} className={`st-tab ${tab === t ? 'on' : ''}`}>
                    {t}
                  </span>
                ))}
              </div>
              <span className="st-right">⤴ Share preview · ⚡ Deployments · david@possibl.ai</span>
            </div>

            <div className="st-body">
              {/* left: plan rail + chat */}
              <div className="st-left">
                <div className="st-plan">
                  <div className="st-plan-head">
                    <span>☰ Build plan</span>
                    <span className="st-phase-badge">{PHASES[phase]}</span>
                  </div>
                  <div className="st-phases">
                    {PHASES.map((p, i) => (
                      <span
                        key={p}
                        className={`st-phase ${i < phase ? 'done' : ''} ${i === phase ? 'active' : ''}`}
                      >
                        {p}
                      </span>
                    ))}
                  </div>
                </div>
                <div className="st-chat" ref={chatRef}>
                  {chat.map((m, i) => (
                    <div key={i} className={`st-msg st-msg-${m.role}`}>
                      {m.role === 'sys' ? <span className="st-check">✓ </span> : null}
                      {m.text}
                    </div>
                  ))}
                </div>
                <div className="st-input">
                  <span className="st-input-ph">Describe the app you want to build…</span>
                  <span className="st-send">Send</span>
                </div>
              </div>

              {/* right: Build preview or Backend inspector */}
              <div className="st-main">
                {tab === 'Build' ? (
                  <>
                    <div className="st-prev-head">
                      <span className="st-repo">◦ possibl-ai/agency-hub</span>
                      <span className={`st-live ${live || preview >= 1 ? 'on' : ''}`}>
                        ● {live ? 'Live' : preview >= 1 ? 'Ready' : 'Starting'}
                      </span>
                      <span className="st-prev-actions">Open · Refresh · Dev URL</span>
                    </div>
                    <div className="st-prev">
                      {preview === 0 ? (
                        <div className="st-prev-empty">▧ preview starting…</div>
                      ) : (
                        <div className="pv-app">
                          <div className="pv-side">
                            <div className="pv-logo" />
                            {['Pipeline', 'Contacts', 'Projects', 'Digest'].map((n, i) => (
                              <div key={n} className={`pv-nav ${i === 0 ? 'on' : ''}`}>
                                {n}
                              </div>
                            ))}
                          </div>
                          <div className="pv-main">
                            <div className="pv-head">
                              <span>Deals pipeline</span>
                              {preview >= 3 && <span className="pv-chip">digest · fri 16:00</span>}
                            </div>
                            {preview >= 2 && (
                              <div className="pv-kpis">
                                <span className="pv-kpi">
                                  <b>$268k</b> pipeline
                                </span>
                                <span className="pv-kpi">
                                  <b>$86k</b> won this month
                                </span>
                                <span className="pv-kpi">
                                  <b>7</b> open deals
                                </span>
                                <span className="pv-kpi">
                                  <b>38%</b> win rate
                                </span>
                              </div>
                            )}
                            <div className="pv-cols">
                              {PIPELINE.map((col, ci) => (
                                <div key={col.name} className="pv-col">
                                  <div className="pv-col-h">
                                    <span>{col.name}</span>
                                    {preview >= 3 && <span className="pv-col-total">{col.total}</span>}
                                  </div>
                                  {preview >= 2 &&
                                    col.deals.map((deal, i) => (
                                      <div
                                        key={deal.co}
                                        className="pv-deal"
                                        style={{ animationDelay: `${(ci * 2 + i) * 140}ms` }}
                                      >
                                        <span className={`pv-av av-${deal.av}`}>{deal.ini}</span>
                                        <span className="pv-deal-body">
                                          <span className="pv-deal-co">{deal.co}</span>
                                          <span className="pv-deal-what">{deal.what}</span>
                                        </span>
                                        <span className="pv-deal-val">{deal.val}</span>
                                      </div>
                                    ))}
                                </div>
                              ))}
                            </div>
                            {preview >= 3 && (
                              <div className="pv-toast">
                                <span className="pv-toast-av">◆</span>
                                <span className="pv-toast-body">
                                  <b>CRM Advisor</b> — 3 deals idle &gt;7 days. Draft follow-ups
                                  for approval?
                                </span>
                                <span className="pv-toast-actions">
                                  <i className="ok">Approve</i>
                                  <i>Skip</i>
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </>
                ) : (
                  <>
                    <div className="st-be-head">
                      <span className="st-be-title">Backend</span>
                      <span className="st-be-sub">your app, as breadcrumbs</span>
                    </div>
                    <div className="st-be-tabs">
                      <span className={counts.agent ? 'on' : ''}>☺ Agents {counts.agent}</span>
                      <span className={counts.tool ? 'on' : ''}>⚒ Tools {counts.tool}</span>
                      <span className={counts.service ? 'on' : ''}>▤ Services {counts.service}</span>
                      <span className={counts.knowledge ? 'on' : ''}>▢ Knowledge {counts.knowledge}</span>
                      <span className={dataCount ? 'on' : ''}>≣ Data {dataCount}</span>
                    </div>
                    <div className="st-be-list">
                      {backend.length === 0 && <div className="st-prev-empty">assembling…</div>}
                      {backend.map((b) => (
                        <div key={b.slug} className="st-be-row">
                          <div>
                            <div className="st-be-name">{b.name}</div>
                            <div className="st-be-slug">{b.slug}</div>
                          </div>
                          <div className="st-be-tags">
                            {b.tags.map((t) => (
                              <span key={t}>{t}</span>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* scrub bar */}
          <div className="demo-scrub">
            <button
              className="demo-play"
              onClick={() => setPlaying((p) => !p)}
              aria-label={playing ? 'pause' : 'play'}
            >
              {playing ? '❚❚' : '▶'}
            </button>
            <div
              className="demo-track"
              onClick={(e) => {
                const r = e.currentTarget.getBoundingClientRect();
                setElapsed(Math.floor(((e.clientX - r.left) / r.width) * TOTAL));
              }}
            >
              <div className="demo-fill" style={{ width: `${(elapsed / TOTAL) * 100}%` }} />
            </div>
            <span className="demo-time">
              {fmt(elapsed)} / {fmt(TOTAL)}
            </span>
          </div>
        </div>

        <p className="ai-note" style={{ marginTop: 18 }}>
          Scripted replay rendered in the real Studio interface — chat, build plan, backend
          inspector, live preview, deploy. Book a session to watch it live on your own use
          case.
        </p>
      </div>
    </section>
  );
}
